/**
 * JUSTIFIED ROW LAYOUT
 * --------------------
 * The photo grid is laid out in justified rows rather than CSS columns: every
 * image in a row shares one height, widths vary with each photo's aspect ratio,
 * and the row fills the container exactly. It's the layout Flickr popularised and
 * that Pixieset and most delivery galleries use, and it beats a column masonry
 * for wedding work because it never crops — a portrait and a landscape sit side
 * by side at their true proportions, and the eye gets clean horizontal bands
 * instead of four ragged columns.
 *
 * The algorithm, per row:
 *   1. Add photos until laying them out at the target height would overflow.
 *   2. Solve for the height that makes the row exactly fill the width:
 *        rowHeight = (containerWidth - totalGaps) / sum(aspectRatios)
 *   3. Each photo's width is then aspectRatio * rowHeight.
 *
 * The final row is deliberately NOT stretched — with one or two photos left over
 * that would blow them up to absurd size, so it keeps the target height instead.
 *
 * Below the mobile breakpoint the whole thing collapses to a single full-width
 * column at natural aspect ratio, which is what phones want: one big photo at a
 * time, no squinting at a third of a row.
 */

const DEFAULT_AR = 3 / 2; // sane fallback if a photo has no stored dimensions

/** Target row height for a given container width (null = single-column mode). */
export function targetRowHeight(containerWidth) {
  if (containerWidth < 640) return null; // phones: one per row, natural height
  if (containerWidth < 900) return 300;
  if (containerWidth < 1200) return 380;
  return 460;
}

const aspect = (p) => (p.width && p.height ? p.width / p.height : DEFAULT_AR);

/**
 * @returns rows of { height, items: [{ photo, index, width }] }
 * `index` is the photo's position in the ORIGINAL array, so the lightbox can
 * open the right one even though rows are built from a partial (paged) list.
 */
export function buildRows(photos, containerWidth, gap) {
  if (!containerWidth || photos.length === 0) return [];
  const target = targetRowHeight(containerWidth);

  // Phone: one photo per row, sized to its own aspect ratio.
  if (target === null) {
    return photos.map((photo, index) => ({
      height: containerWidth / aspect(photo),
      items: [{ photo, index, width: containerWidth }],
    }));
  }

  const rows = [];
  let current = [];
  let arSum = 0;

  photos.forEach((photo, index) => {
    const ar = aspect(photo);
    current.push({ photo, index, ar });
    arSum += ar;

    // Width this row would occupy if drawn at the target height.
    const projected = arSum * target + gap * (current.length - 1);
    if (projected >= containerWidth) {
      const available = containerWidth - gap * (current.length - 1);
      const height = available / arSum;
      rows.push({ height, items: current.map((it) => ({ ...it, width: it.ar * height })) });
      current = [];
      arSum = 0;
    }
  });

  // Leftovers keep the target height rather than stretching to fill.
  if (current.length) {
    rows.push({
      height: target,
      items: current.map((it) => ({ ...it, width: it.ar * target })),
      isLast: true,
    });
  }

  return rows;
}
